import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Badge, Button, Form, Alert, Spinner, Dropdown } from 'react-bootstrap';
import { FaArrowLeft, FaUser, FaShoppingCart, FaReply, FaPaperPlane, FaClock, FaCheckCircle, FaTimesCircle, FaExclamationTriangle } from 'react-icons/fa';
import PageHeader from '../components/PageHeader';
import supportRequestService from '../services/supportRequestService';

function SupportRequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [supportRequest, setSupportRequest] = useState(null);
  const [replies, setReplies] = useState([]);
  const [userDetails, setUserDetails] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const fetchSupportRequestDetails = useCallback(async () => {
    try {
      setLoading(true);
      const data = await supportRequestService.getById(id);
      setSupportRequest(data.support_request);
      setReplies(data.replies || []);
      setUserDetails(data.user_details);
      setOrderDetails(data.order_details);
    } catch (err) {
      setError('Failed to load support request details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSupportRequestDetails();
  }, [fetchSupportRequestDetails]);

  const handleStatusUpdate = async (newStatus) => {
    try {
      setUpdatingStatus(true);
      await supportRequestService.updateStatus(supportRequest.id, newStatus);
      setSupportRequest(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      setError('Failed to update status');
      console.error(err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) return;

    try {
      setSubmittingReply(true);
      const data = await supportRequestService.addReply(supportRequest.id, replyMessage.trim());
      setReplies(prev => [...prev, data.reply]);
      setReplyMessage('');
    } catch (err) {
      setError('Failed to send reply');
      console.error(err);
    } finally {
      setSubmittingReply(false);
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'open': return 'danger';
      case 'in_progress': return 'secondary';
      case 'resolved': return 'success';
      case 'closed': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <FaExclamationTriangle />;
      case 'in_progress': return <FaClock />;
      case 'resolved': return <FaCheckCircle />;
      case 'closed': return <FaTimesCircle />;
      default: return <FaClock />;
    }
  };

  const getPriorityBadgeVariant = (priority) => {
    switch (priority) {
      case 'urgent': return 'danger';
      case 'high': return 'primary';
      case 'normal': return 'secondary';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!supportRequest) {
    return (
      <Container fluid className="px-4">
        <Alert variant="danger">
          Support request not found
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="px-4">
      <PageHeader
        title={`Support Request #${supportRequest.id}`}
        subtitle={`Created ${formatDate(supportRequest.created_at)}`}
      />

      <Row className="mb-3">
        <Col>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate('/support-requests')}
          >
            <FaArrowLeft className="me-2" />
            Back to Support Requests
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Row className="g-4">
        {/* Support Request Details */}
        <Col lg={8}>
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-1">{supportRequest.subject}</h5>
                <div className="d-flex gap-2">
                  <Badge bg={getStatusBadgeVariant(supportRequest.status)} className="d-flex align-items-center">
                    {getStatusIcon(supportRequest.status)}
                    <span className="ms-1 text-capitalize">{supportRequest.status.replace('_', ' ')}</span>
                  </Badge>
                  <Badge bg={getPriorityBadgeVariant(supportRequest.priority)} className="text-capitalize">
                    {supportRequest.priority} Priority
                  </Badge>
                </div>
              </div>
              <Dropdown>
                <Dropdown.Toggle
                  variant="dark"
                  size="sm"
                  disabled={updatingStatus}
                >
                  {updatingStatus ? 'Updating...' : 'Change Status'}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => handleStatusUpdate('open')}>Open</Dropdown.Item>
                  <Dropdown.Item onClick={() => handleStatusUpdate('in_progress')}>In Progress</Dropdown.Item>
                  <Dropdown.Item onClick={() => handleStatusUpdate('resolved')}>Resolved</Dropdown.Item>
                  <Dropdown.Item onClick={() => handleStatusUpdate('closed')}>Closed</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Card.Header>
            <Card.Body>
              <h6>Message:</h6>
              <p className="text-muted mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                {supportRequest.message}
              </p>
            </Card.Body>
          </Card>

          {/* Replies Section */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header>
              <h5 className="mb-0">Conversation ({replies.length} replies)</h5>
            </Card.Header>
            <Card.Body className="p-0">
              {replies.length === 0 ? (
                <div className="p-4 text-center text-muted">
                  <FaReply className="mb-2" size={24} />
                  <p>No replies yet</p>
                </div>
              ) : (
                <div className="p-3">
                  {replies.map((reply, index) => (
                    <div key={reply.id} className={`mb-3 ${index < replies.length - 1 ? 'border-bottom pb-3' : ''}`}>
                      <div className="d-flex align-items-start">
                        <div className={`rounded-circle d-flex align-items-center justify-content-center text-white fw-bold me-3 ${
                          reply.is_admin ? 'bg-primary' : 'bg-secondary'
                        }`} style={{ width: '40px', height: '40px', fontSize: '14px' }}>
                          {reply.is_admin ? 'A' : 'U'}
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center mb-2">
                            <span className="fw-semibold me-2">
                              {reply.is_admin ? 'Admin' : 'Customer'}
                            </span>
                            <small className="text-muted">
                              {formatDate(reply.created_at)}
                            </small>
                          </div>
                          <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                            {reply.message}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Reply Form */}
              <div className="border-top p-3">
                <Form onSubmit={handleReplySubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Add Reply</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      placeholder="Type your reply here..."
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      required
                    />
                  </Form.Group>
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    disabled={submittingReply || !replyMessage.trim()}
                  >
                    {submittingReply ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <FaPaperPlane className="me-2" />
                        Send Reply
                      </>
                    )}
                  </Button>
                </Form>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Sidebar */}
        <Col lg={4}>
          {/* User Details */}
          {userDetails && (
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header>
                <h6 className="mb-0 d-flex align-items-center">
                  <FaUser className="me-2" />
                  Customer Details
                </h6>
              </Card.Header>
              <Card.Body>
                <div className="mb-2">
                  <strong>Username:</strong> {userDetails.username}
                </div>
                <div className="mb-2">
                  <strong>Email:</strong> {userDetails.email}
                </div>
                <div>
                  <strong>Member since:</strong> <small className="text-muted">{formatDate(userDetails.created_at)}</small>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Order Details */}
          {orderDetails && (
            <Card className="border-0 shadow-sm mb-4">
              <Card.Header>
                <h6 className="mb-0 d-flex align-items-center">
                  <FaShoppingCart className="me-2" />
                  Order Details
                </h6>
              </Card.Header>
              <Card.Body>
                <div className="mb-2">
                  <strong>Order ID:</strong> #{orderDetails.id}
                </div>
                <div className="mb-2">
                  <strong>Total Amount:</strong> ${orderDetails.total_amount?.toFixed(2)}
                </div>
                <div className="mb-2">
                  <strong>Status:</strong>{' '}
                  <Badge bg={orderDetails.status === 'completed' ? 'success' : 'warning'}>
                    {orderDetails.status}
                  </Badge>
                </div>
                <div className="mb-2">
                  <strong>Items:</strong> {orderDetails.items_count}
                </div>
                <div>
                  <strong>Ordered on:</strong> <small className="text-muted">{formatDate(orderDetails.created_at)}</small>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Request Info */}
          <Card className="border-0 shadow-sm">
            <Card.Header>
              <h6 className="mb-0">Request Information</h6>
            </Card.Header>
            <Card.Body>
              <div className="mb-2">
                <strong>Created:</strong> <small className="text-muted">{formatDate(supportRequest.created_at)}</small>
              </div>
              {supportRequest.responded_at && (
                <div className="mb-2">
                  <strong>First Response:</strong> <small className="text-muted">{formatDate(supportRequest.responded_at)}</small>
                </div>
              )}
              {supportRequest.resolved_at && (
                <div className="mb-2">
                  <strong>Resolved:</strong> <small className="text-muted">{formatDate(supportRequest.resolved_at)}</small>
                </div>
              )}
              <div>
                <strong>Last Updated:</strong> <small className="text-muted">{formatDate(supportRequest.updated_at)}</small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default SupportRequestDetailPage;